export const MERCATOR_MAX_LAT = 85.05112878;
const HEX_TILE_SIZE = 512;

export interface WorldPoint {
  x: number;
  y: number;
}

export interface HexCell {
  id: string;
  q: number;
  r: number;
}

export function clampLat(lat: number): number {
  return Math.max(-MERCATOR_MAX_LAT, Math.min(MERCATOR_MAX_LAT, lat));
}

export function worldScale(zoom: number): number {
  return HEX_TILE_SIZE * Math.pow(2, zoom);
}

export function hexRes(zoom: number): number {
  if (zoom < 3) return 1;
  if (zoom < 5) return 2;
  if (zoom < 7) return 3;
  if (zoom < 9) return 4;
  if (zoom < 11) return 5;
  if (zoom < 13) return 6;
  return 7;
}

export function hexGridAnchorZoom(res: number): number {
  if (res <= 0) return 1;
  if (res === 1) return 3;
  if (res === 2) return 5;
  if (res === 3) return 7;
  if (res === 4) return 9;
  if (res === 5) return 11;
  if (res === 6) return 13;
  return 15;
}

export function hexPixelRadiusForRes(res: number): number {
  if (res <= 0) return 34;
  if (res === 1) return 38;
  if (res === 2) return 42;
  if (res === 3) return 46;
  if (res === 4) return 50;
  if (res === 5) return 54;
  if (res === 6) return 58;
  return 62;
}

export function lngLatToWorld(lng: number, lat: number, scale: number): WorldPoint {
  const phi = (clampLat(lat) * Math.PI) / 180;
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI)) * scale,
  };
}

export function worldToLngLat(x: number, y: number, scale: number): [number, number] {
  const lng = (x / scale) * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI - (2 * Math.PI * y) / scale)) * 180) / Math.PI;
  return [lng, clampLat(lat)];
}

function axialRound(q: number, r: number): { q: number; r: number } {
  let x = q;
  let z = r;
  let y = -x - z;
  let rx = Math.round(x);
  let ry = Math.round(y);
  let rz = Math.round(z);
  const xDiff = Math.abs(rx - x);
  const yDiff = Math.abs(ry - y);
  const zDiff = Math.abs(rz - z);
  if (xDiff > yDiff && xDiff > zDiff) rx = -ry - rz;
  else if (yDiff > zDiff) ry = -rx - rz;
  else rz = -rx - ry;
  return { q: rx, r: rz };
}

export function worldToHexCell(x: number, y: number, res: number): HexCell {
  const size = hexPixelRadiusForRes(res);
  const q = ((2 / 3) * x) / size;
  const r = ((-1 / 3) * x + (Math.sqrt(3) / 3) * y) / size;
  const rounded = axialRound(q, r);
  return { ...rounded, id: `WM${res}:${rounded.q}:${rounded.r}` };
}

export function hexCellCenter(q: number, r: number, res: number): WorldPoint {
  const size = hexPixelRadiusForRes(res);
  return {
    x: size * ((3 / 2) * q),
    y: size * (Math.sqrt(3) * (r + q / 2)),
  };
}
